import {
  ActionButton,
  OfficeButton,
  Paginator,
  TableComponent,
  TableLoader,
} from "components";
import { useMatch, useNavigate, useSearch } from "react-location";
import { useUrlState, withPermissions } from "utils";

import { Action } from "components/buttons/action-button";
import CreateInsurerManagerContainer from "pages/re-insurers/add-manager";
import { LocationGenerics } from "router/location";
import RemoveContainer from "./remove-associate";
import UpdateManagerContainer from "./update-associates";
import { UserPlusIcon } from "@heroicons/react/24/outline";
import ViewReinsurerAssociateContainer from "./view-associate";
import { usePagination } from "hooks";
import { useReInsurerAssociates } from "hooks/data/use-re-insurers";

type Props = {};

const Associates = (props: Props) => {
  const { params } = useMatch<LocationGenerics>();
  const searchParams = useSearch<LocationGenerics>();
  const navigate = useNavigate<LocationGenerics>();
  const [modal, setModal] = useUrlState("modal");
  const { limit, offset, setPage } = usePagination();
  const { loading, associates, refetch, count } = useReInsurerAssociates({
    reinsurerId: params?.reInsurer as string,
    pagination: {
      limit: limit,
      offset: offset,
    },
    search: searchParams?.search || "",
  });

  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
    () => {
      navigate({
        search: (old) => ({
          ...old,
          id,
          modal: action,
        }),
      });
    };

  return (
    <div className="pt-6">
      <TableComponent
        hasSearch
        title={"associates"}
        columns={[
          {
            accessor: "rep_first_name",
            name: "First Name",
          },
          {
            accessor: "rep_last_name",
            name: "Last Name",
          },
          {
            accessor: "rep_email",
            name: "Email",
          },
          {
            accessor: "reinsurer.re_company_phone_number",
            name: "Phone Number",
            render: (value) => (
              <div className="flex flex-col">
                <span>{value?.rep_primary_phonenumber}</span>
                <span>{value?.rep_secondary_phonenumber}</span>
              </div>
            ),
          },
          {
            accessor: "position",
            name: "Actions",
            render: (value) => (
              <>
                <ActionButton
                  action={"view"}
                  tooltip="View Associate"
                  onClick={dispatchAction(
                    value.reinsurer_representative_id || "",
                    "view"
                  )}
                />
                {withPermissions([
                  "*:*",
                  "reinsurers:*",
                  "reinsurers:manage",
                  "reinsurers:update",
                ])(
                  <ActionButton
                    action={"update"}
                    tooltip="Edit Associate"
                    onClick={dispatchAction(
                      value.reinsurer_representative_id || "",
                      "update"
                    )}
                  />
                )}
                {withPermissions([
                  "*:*",
                  "reinsurers:*",
                  "reinsurers:manage",
                  "reinsurers:delete",
                ])(
                  <ActionButton
                    action={"remove"}
                    tooltip="Remove Associate"
                    onClick={dispatchAction(
                      value.reinsurer_representative_id || "",
                      "remove"
                    )}
                  />
                )}
              </>
            ),
          },
        ]}
        renderLoader={() => <TableLoader items={[1, 1, 1, 2, 1]} />}
        pagination={() => (
          <Paginator
            offset={offset}
            limit={limit}
            totalItems={count}
            currentSize={associates.length}
            setOffset={setPage}
          />
        )}
        data={associates}
        loading={loading}
        refetch={refetch}
        renderHeaderItems={() => (
          <>
            {" "}
            {withPermissions([
              "*:*",
              "reinsurers:*",
              "reinsurers:manage",
              "reinsurers:create",
            ])(
              <OfficeButton onClick={dispatchAction("", "create")}>
                <span>Add Associate</span>
                <UserPlusIcon className="h-6 w-6" />
              </OfficeButton>
            )}{" "}
          </>
        )}
      />

      <CreateInsurerManagerContainer
        open={modal === "create"}
        setOpen={(val: boolean) => setModal(val ? "create" : undefined)}
      />

      {searchParams?.id && (
        <>
          <ViewReinsurerAssociateContainer
            open={modal === "view"}
            setOpen={(val: boolean) => setModal(val ? "view" : undefined)}
          />
          <RemoveContainer
            open={modal === "remove"}
            setOpen={(val: boolean) => setModal(val ? "remove" : undefined)}
          />
          <UpdateManagerContainer
            open={modal === "update"}
            setOpen={(val: boolean) => setModal(val ? "update" : undefined)}
          />
        </>
      )}
    </div>
  );
};

export default Associates;
